import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  BookOpen, Copy, ExternalLink, Layers, Network, Server, Shield,
  Boxes, Code2, Search, Loader2, FileDown, Cloud,
} from "lucide-react";

const METHOD_COLORS = {
  GET: "border-success/40 text-success bg-success/5",
  POST: "border-primary/40 text-primary bg-primary/5",
  PATCH: "border-warning/40 text-warning bg-warning/5",
  PUT: "border-warning/40 text-warning bg-warning/5",
  DELETE: "border-destructive/40 text-destructive bg-destructive/5",
};

function MetricCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="font-display text-3xl font-bold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function copyToClipboard(text, label = "Copied") {
  navigator.clipboard?.writeText(text);
  toast.success(label);
}

export default function AdminPlatformOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [guides, setGuides] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: d }, { data: g }] = await Promise.all([
          api.get("/admin/platform/overview"),
          api.get("/admin/platform/guides").catch(() => ({ data: { guides: [] } })),
        ]);
        setData(d);
        setGuides(g.guides || []);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function downloadGuide(guide) {
    setDownloadingId(guide.id);
    try {
      const res = await api.get(guide.endpoint.replace("/api", ""), {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = guide.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded ${guide.filename}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  }

  const filteredSections = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.api_sections;
    return data.api_sections
      .map((s) => ({
        ...s,
        endpoints: s.endpoints.filter(
          (e) =>
            e.path.toLowerCase().includes(q) ||
            (e.summary || "").toLowerCase().includes(q) ||
            e.method.toLowerCase().includes(q)
        ),
      }))
      .filter((s) => s.endpoints.length > 0);
  }, [data, search]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh] text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading platform overview…
        </div>
      </DashboardLayout>
    );
  }
  if (!data) return null;

  const platform = data.platform;
  const counts = data.counts;

  return (
    <DashboardLayout>
      <div className="animate-fade-up" data-testid="admin-platform-overview">
        <div className="flex flex-col gap-2 mb-6">
          <span className="overline">Platform</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            {platform.name}
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            One-stop view of every module, capability, and API endpoint exposed by this
            platform. Use it for partner onboarding briefs, internal handoffs, or as a
            reference while building integrations.
          </p>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="font-mono text-[11px]" data-testid="platform-version">
              {platform.version}
            </Badge>
            <Badge variant="outline" className="font-mono text-[11px]">SDK · {data.sdk.language} · {data.sdk.version}</Badge>
          </div>
        </div>

        {/* Top metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          <MetricCard icon={Boxes} label="Features" value={counts.total_features} testid="metric-features" />
          <MetricCard icon={Shield} label="Roles" value={counts.total_roles} testid="metric-roles" />
          <MetricCard icon={Network} label="API Sections" value={counts.total_api_sections} testid="metric-sections" />
          <MetricCard icon={Code2} label="Endpoints" value={counts.total_documented_endpoints} testid="metric-endpoints" />
          <MetricCard icon={Server} label="Active SDKs" value={counts.active_sdk_clients} testid="metric-sdks" />
          <MetricCard icon={Layers} label="Partners" value={counts.total_partners} testid="metric-partners" />
        </div>

        {/* Deployment & migration guides */}
        {guides.length > 0 && (
          <Card className="mb-8 border-primary/30 bg-primary/[0.02]" data-testid="deployment-guides-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cloud className="h-5 w-5 text-primary" /> Deployment & Migration Guides
              </CardTitle>
              <CardDescription>
                Downloadable PDFs that walk you through hosting FlexPay on your own cloud infrastructure.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {guides.map((g) => (
                <div
                  key={g.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-border bg-card p-4"
                  data-testid={`guide-${g.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-display font-semibold text-base">{g.title}</span>
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {g.pages} pages
                      </Badge>
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {g.size_kb} KB
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">
                        {g.audience}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {g.description}
                    </p>
                  </div>
                  <Button
                    onClick={() => downloadGuide(g)}
                    disabled={!g.available || downloadingId === g.id}
                    className="shrink-0"
                    data-testid={`guide-download-${g.id}`}
                  >
                    {downloadingId === g.id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Downloading…
                      </>
                    ) : (
                      <>
                        <FileDown className="h-4 w-4 mr-2" /> Download PDF
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="api" className="w-full" data-testid="platform-tabs">
          <TabsList className="grid grid-cols-3 sm:w-fit mb-4">
            <TabsTrigger value="api" data-testid="tab-api">API Documentation</TabsTrigger>
            <TabsTrigger value="features" data-testid="tab-features">Feature Inventory</TabsTrigger>
            <TabsTrigger value="sdk" data-testid="tab-sdk">SDK & Discovery</TabsTrigger>
          </TabsList>

          {/* API Docs */}
          <TabsContent value="api" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" /> Base URLs & Reference
                </CardTitle>
                <CardDescription>
                  All endpoints are prefixed with <code className="font-mono text-foreground">/api</code>. Authentication uses a Bearer JWT (or OAuth client_credentials for SDKs).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  { label: "API Base", value: platform.api_base, testid: "url-api" },
                  { label: "Discovery (.well-known)", value: platform.discovery_url, testid: "url-discovery" },
                  { label: "Swagger UI", value: platform.swagger_ui_url, testid: "url-swagger" },
                  { label: "OpenAPI JSON", value: platform.openapi_url, testid: "url-openapi" },
                  { label: "ReDoc", value: platform.redoc_url, testid: "url-redoc" },
                ].map((row) => (
                  <div key={row.label} className="flex items-center gap-2 text-sm" data-testid={row.testid}>
                    <span className="w-44 text-muted-foreground">{row.label}</span>
                    <code className="flex-1 font-mono text-xs bg-muted/50 px-2 py-1.5 rounded truncate">{row.value}</code>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(row.value)}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <a href={row.value} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="ghost"><ExternalLink className="h-3.5 w-3.5" /></Button>
                    </a>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search endpoints by path, method, or summary…"
                className="max-w-md"
                data-testid="api-search"
              />
              <Badge variant="outline" className="ml-auto" data-testid="api-result-count">
                {filteredSections.reduce((acc, s) => acc + s.endpoints.length, 0)} endpoints
              </Badge>
            </div>

            <div className="space-y-4">
              {filteredSections.length === 0 && (
                <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground" data-testid="api-empty">
                  No endpoints match "{search}".
                </div>
              )}
              {filteredSections.map((section) => (
                <Card key={section.id} data-testid={`section-${section.id}`}>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center justify-between flex-wrap gap-2">
                      <span>{section.title}</span>
                      <Badge variant="outline" className="text-[10px]">{section.endpoints.length} endpoints</Badge>
                    </CardTitle>
                    <CardDescription>{section.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-1.5">
                    {section.endpoints.map((ep) => (
                      <div
                        key={`${ep.method}-${ep.path}`}
                        className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-accent/40 transition-colors group"
                        data-testid={`endpoint-${section.id}`}
                      >
                        <Badge
                          variant="outline"
                          className={`text-[10px] font-mono w-16 justify-center ${METHOD_COLORS[ep.method] || ""}`}
                        >
                          {ep.method}
                        </Badge>
                        <code className="font-mono text-[12px] truncate flex-1">{ep.path}</code>
                        <span className="text-xs text-muted-foreground hidden sm:block flex-1 truncate">{ep.summary}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => copyToClipboard(ep.path, "Path copied")}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Feature inventory */}
          <TabsContent value="features" className="space-y-4">
            <p className="text-sm text-muted-foreground max-w-3xl">
              Every feature flag exposed in the Feature Matrix, grouped by module. Limits
              are configurable per role from the Feature Matrix page.
            </p>
            {Object.entries(data.features_by_group).map(([group, items]) => (
              <Card key={group} data-testid={`feature-group-${group}`}>
                <CardHeader>
                  <CardTitle className="text-base capitalize">{group.replaceAll("_", " ")}</CardTitle>
                  <CardDescription>{items.length} feature{items.length === 1 ? "" : "s"}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {items.map((f) => (
                      <div
                        key={f.key}
                        className="rounded-md border border-border p-2.5 text-sm"
                        data-testid={`feature-${f.key}`}
                      >
                        <div className="font-medium">{f.label}</div>
                        <div className="flex items-center justify-between gap-2 mt-1">
                          <code className="font-mono text-[10px] text-muted-foreground">{f.key}</code>
                          {f.has_limit && (
                            <Badge variant="outline" className="text-[9px]">
                              limit {f.default_limit ?? "—"}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* SDK */}
          <TabsContent value="sdk" className="space-y-4">
            <Card data-testid="sdk-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code2 className="h-5 w-5" /> {data.sdk.language} SDK · v{data.sdk.version}
                </CardTitle>
                <CardDescription>
                  Auth: {data.sdk.auth} · Package: <code className="font-mono">{data.sdk.package_name}</code>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">{data.sdk.discovery_pattern}</p>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Namespaces</div>
                  <div className="flex flex-wrap gap-2">
                    {data.sdk.namespaces.map((n) => (
                      <Badge key={n} variant="outline" className="font-mono text-[11px]">{n}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs text-muted-foreground">Quick start</div>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(data.sdk.code_sample_dart, "Snippet copied")}>
                      <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                    </Button>
                  </div>
                  <pre className="bg-muted/40 rounded-md p-3 text-xs font-mono whitespace-pre overflow-x-auto" data-testid="sdk-snippet">
{data.sdk.code_sample_dart}
                  </pre>
                </div>
                <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
                  <div className="font-semibold mb-1">Need credentials?</div>
                  <div className="text-muted-foreground text-xs">
                    Issue a client_id + secret from <code className="font-mono">/admin/sdk-clients</code>. Secrets are shown once at creation time.
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
